import { adjustContentVisibility, Section, SectionTitle } from '@/components/shared/Content';
import JoinCTA from '@/components/shared/JoinCTA';
import useLottie from '@/hooks//useLottie';
import { Box, Flex, font } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import useEmblaCarousel from 'embla-carousel-react';
import parse from 'html-react-parser';
import type { AnimationItem } from 'lottie-web';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';
import { SliderDotButton, SliderDotButtonWrapper } from '../shared/slider/SliderDotButton';
import { useSliderDotButton } from '../shared/slider/useSliderDotButton';

const SwitchingTitle = styled(SectionTitle)`
  ${MEDIA_QUERIES.mobile`
        padding-bottom: 40px;
    `}

  ${MEDIA_QUERIES.tablet`
        white-space: initial;
    `}

    ${MEDIA_QUERIES.desktop`
        white-space: pre;
    `}
`;

const Lottie = styled(Box)`
  height: 230px;
  width: 286px;
  margin: 0 auto;
`;

const StepText = styled.span`
  ${font('main', { fontSize: '22px', fontWeight: 700 })};
  text-align: center;
  white-space: pre;
  padding-top: 10px;
  display: block;

  a {
    text-decoration: underline !important;
  }

  ${MEDIA_QUERIES.tabletAndDesktop`
    ${font('main', { fontSize: '28px', fontWeight: 700 })};
  `}
`;

const StepsContainer = styled(Flex)`
  width: 286px;
  ${MEDIA_QUERIES.tablet`
        transform: scale(0.8);
    `}
`;

const CTA = styled(JoinCTA)`
  &&& {
    display: block;
  }
`;

const Wrapper = styled(Flex)`
  display: none;
  ${MEDIA_QUERIES.tabletAndDesktop`
    display: flex;
  `}
`;

const CarouseContainer = styled(Flex)`
  ${MEDIA_QUERIES.tabletAndDesktop`
    display: none;
  `}
  ${MEDIA_QUERIES.mobile`
    display: flex;
  `}
`;

const StyledSection = styled(Section)`
  ${adjustContentVisibility}
`;

type PlayAnimation = { playAnimation?: boolean };

interface LottieAnimationProps extends PlayAnimation, Pick<SwitchingEuropeProps, 'steps'> {
  leftDesktopLottieConfig: unknown;
  centerDesktopLottieConfig: unknown;
  rightDesktopLottieConfig: unknown;
}

interface SliderAnimationProps extends PlayAnimation, Pick<SwitchingEuropeProps, 'steps'> {
  leftLottieConfig: unknown;
  centerLottieConfig: unknown;
  rightLottieConfig: unknown;
}

const LottieAnimation: React.FC<LottieAnimationProps> = ({
  leftDesktopLottieConfig,
  centerDesktopLottieConfig,
  rightDesktopLottieConfig,
  playAnimation,
  steps,
}) => {
  const leftLottieRef = useRef<HTMLDivElement>(null);
  const centerLottieRef = useRef<HTMLDivElement>(null);
  const rightLottieRef = useRef<HTMLDivElement>(null);

  const leftLottie = useLottie(leftLottieRef, {
    animationData: leftDesktopLottieConfig,
    lottieConfig: {
      loop: false,
    },
  }) as AnimationItem | null;

  const centerLottie = useLottie(centerLottieRef, {
    animationData: centerDesktopLottieConfig,
    lottieConfig: {
      loop: false,
    },
  }) as AnimationItem | null;

  const rightLottie = useLottie(rightLottieRef, {
    animationData: rightDesktopLottieConfig,
    lottieConfig: {
      loop: false,
    },
  }) as AnimationItem | null;

  useEffect(() => {
    if (leftLottie && playAnimation) {
      leftLottie.play();
    }
    if (centerLottie && playAnimation) {
      centerLottie.play();
    }
    if (rightLottie && playAnimation) {
      rightLottie.play();
    }
  }, [leftLottie, centerLottie, rightLottie, playAnimation]);
  return (
    <Wrapper as="ul" mx={20} pb={[60, 40]} pt={76} justifyContent="center" alignContent="space-between">
      <StepsContainer as="li" flexDirection="column">
        <Lottie as="span" ref={leftLottieRef} />
        <StepText>{parse(steps[0])}</StepText>
      </StepsContainer>
      <StepsContainer as="li" flexDirection="column" mx={[0, 0, 93]}>
        <Lottie as="span" ref={centerLottieRef} />
        <StepText>{parse(steps[1])}</StepText>
      </StepsContainer>
      <StepsContainer as="li" flexDirection="column">
        <Lottie as="span" ref={rightLottieRef} />
        <StepText>{parse(steps[2])}</StepText>
      </StepsContainer>
    </Wrapper>
  );
};

const Carousel: React.FC<SliderAnimationProps> = ({
  leftLottieConfig,
  centerLottieConfig,
  rightLottieConfig,
  playAnimation,
  steps,
}) => {
  const leftSliderLottieRef = useRef<HTMLDivElement>(null);
  const centerSliderLottieRef = useRef<HTMLDivElement>(null);
  const rightSliderLottieRef = useRef<HTMLDivElement>(null);

  const [emblaRef, slideApi] = useEmblaCarousel({
    containScroll: false,
  });

  const leftSliderLottie = useLottie(leftSliderLottieRef, {
    animationData: leftLottieConfig,
    lottieConfig: {
      loop: false,
    },
  }) as AnimationItem | null;

  const centerSliderLottie = useLottie(centerSliderLottieRef, {
    animationData: centerLottieConfig,
    lottieConfig: {
      loop: false,
    },
  }) as AnimationItem | null;

  const rightSliderLottie = useLottie(rightSliderLottieRef, {
    animationData: rightLottieConfig,
    lottieConfig: {
      loop: false,
    },
  }) as AnimationItem | null;

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useSliderDotButton(slideApi);
  const isAutoPlayingRef = useRef(true);

  const handleDotClick = (index: number) => {
    isAutoPlayingRef.current = false;
    onDotButtonClick(index);
  };

  useEffect(() => {
    if (!slideApi || !leftSliderLottie || !centerSliderLottie || !rightSliderLottie || !playAnimation) return;

    const playCurrentSlide = () => {
      const currentSnap = slideApi.selectedScrollSnap();
      if (currentSnap === 0) {
        leftSliderLottie.play();
      } else if (currentSnap === 1) {
        centerSliderLottie.play();
      } else if (currentSnap === 2) {
        rightSliderLottie.play();
      }
    };

    const onLeftComplete = () => {
      if (isAutoPlayingRef.current) slideApi.scrollTo(1);
    };
    const onCenterComplete = () => {
      if (isAutoPlayingRef.current) slideApi.scrollTo(2);
    };
    const onRightComplete = () => {
      if (isAutoPlayingRef.current) slideApi.scrollTo(0);
    };

    leftSliderLottie.addEventListener('complete', onLeftComplete);
    centerSliderLottie.addEventListener('complete', onCenterComplete);
    rightSliderLottie.addEventListener('complete', onRightComplete);

    slideApi.on('select', playCurrentSlide);

    playCurrentSlide();

    return () => {
      slideApi.off('select', playCurrentSlide);
      leftSliderLottie.removeEventListener('complete', onLeftComplete);
      centerSliderLottie.removeEventListener('complete', onCenterComplete);
      rightSliderLottie.removeEventListener('complete', onRightComplete);
    };
  }, [slideApi, leftSliderLottie, centerSliderLottie, rightSliderLottie, playAnimation]);

  return (
    <CarouseContainer overflow="hidden" ref={emblaRef} flexDirection="column">
      <Flex gap="20px" mb="16px">
        <Flex flex="0 0 50%" flexDirection="column" key={0}>
          <Lottie ref={leftSliderLottieRef} />
          <StepText>{parse(steps[0])}</StepText>
        </Flex>
        <Flex flex="0 0 50%" flexDirection="column" key={1}>
          <Lottie ref={centerSliderLottieRef} />
          <StepText>{parse(steps[1])}</StepText>
        </Flex>
        <Flex flex="0 0 50%" flexDirection="column" key={2}>
          <Lottie ref={rightSliderLottieRef} />
          <StepText>{parse(steps[2])}</StepText>
        </Flex>
      </Flex>

      <SliderDotButtonWrapper>
        {scrollSnaps.map((_, index) => (
          <SliderDotButton key={index} onClick={() => handleDotClick(index)} isActive={index === selectedIndex} />
        ))}
      </SliderDotButtonWrapper>
    </CarouseContainer>
  );
};

export interface SwitchingEuropeProps extends LottieAnimationProps, SliderAnimationProps {
  title: string;
  ctaLabel: string;

  ctaUrl?: string;
  steps: [string, string, string];
}

export const SwitchingEurope: React.FC<SwitchingEuropeProps> = ({ title, ctaLabel, ctaUrl, ...props }) => {
  const [playAnimation, setPlayAnimation] = useState(false);
  const { ref, inView } = useInView({
    threshold: 0,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) {
      setPlayAnimation(true);
    }
  }, [inView]);

  return (
    <StyledSection pt={[50, 80]} pb={[40, 70]} px="15px" id="switching">
      <SwitchingTitle id="switching-eu-title" tabIndex={0}>
        {title}
      </SwitchingTitle>
      <Carousel {...props} playAnimation={playAnimation} />
      <LottieAnimation {...props} playAnimation={playAnimation} />
      <Box mt={40} ref={ref}>
        <CTA flavor="primary" glowing id="gtm_button_switch_to_lemonade" data-source="switching_cta" customUrl={ctaUrl}>
          {ctaLabel}
        </CTA>
      </Box>
    </StyledSection>
  );
};
